import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  Download, Send, Edit, Briefcase, User, MapPin, Phone, Mail, 
  Calendar, Clock, CheckCircle, AlertCircle, FileText, Image,
  X, ChevronDown, ChevronUp, MessageSquare, DollarSign, Percent,
  Building, ExternalLink, Loader2, Receipt, Plus, Trash2, Users,
  UserCheck, Megaphone, Wrench
} from 'lucide-react';
import InvoiceView from './InvoiceView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function QuoteView({ 
  open, 
  onOpenChange, 
  quoteId,
  onEdit,
  onRefresh,
  autoOpenConvert = false
}) {
  const [loading, setLoading] = useState(true);
  const [quoteData, setQuoteData] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [handymen, setHandymen] = useState([]);
  const [approving, setApproving] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Auto-open convert dialog when prop is true
  useEffect(() => {
    if (autoOpenConvert && open && quoteData && !loading) {
      setShowConvertDialog(true);
    }
  }, [autoOpenConvert, open, quoteData, loading]);
  
  // Email compose dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: '',
    includePdf: true
  });
  
  // SMS compose dialog state
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [smsData, setSmsData] = useState({
    to: '',
    message: ''
  });
  
  // Invoice form state
  const [invoiceData, setInvoiceData] = useState({
    due_days: 30,
    notes: '',
    include_payment_terms: true
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  
  // Team members state
  const [supervisors, setSupervisors] = useState([]);
  const [marketingTeam, setMarketingTeam] = useState([]);
  
  // Approval form state
  const [approvalMethod, setApprovalMethod] = useState('other_communication');
  const [approveNote, setApproveNote] = useState('');
  
  // Convert form state - Enhanced for multiple assignments
  const [convertData, setConvertData] = useState({
    handymen: [{ handyman_id: '', scheduled_date: '', notes: '' }],
    supervisor_id: '',
    marketing_ids: [],
    job_notes: ''
  });

  useEffect(() => {
    if (open && quoteId) {
      fetchQuoteData();
      fetchHandymen();
      fetchTeamMembers();
    }
  }, [open, quoteId]);

  // Initialize email/sms data when quote loads
  useEffect(() => {
    if (quoteData?.quote) {
      setEmailData({
        to: quoteData.quote.customer_email || '',
        subject: `Quote #${quoteData.quote.quote_number} from Redefyne Interior and Exterior`,
        message: `Dear ${quoteData.quote.customer_name},\n\nPlease find attached your quote for ${quoteData.quote.service_type || 'our services'}.\n\nTotal Amount: $${quoteData.quote.total_amount?.toFixed(2)}\n\nPlease review and let us know if you have any questions.\n\nBest regards,\nRedefyne Interior and Exterior`,
        includePdf: true
      });
      setSmsData({
        to: quoteData.quote.customer_phone || '',
        message: `Hi ${quoteData.quote.customer_name}, your quote #${quoteData.quote.quote_number} for $${quoteData.quote.total_amount?.toFixed(2)} is ready. Please check your email for details. - Redefyne`
      });
    }
  }, [quoteData]);

  const fetchQuoteData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/quotes/${quoteId}/full`);
      setQuoteData(response.data);
    } catch (error) {
      toast.error('Failed to load quote details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchHandymen = async () => {
    try {
      const response = await axiosInstance.get('/handymen');
      setHandymen(response.data.filter(h => h.is_available));
    } catch (error) {
      console.error('Failed to fetch handymen');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      const users = response.data || [];
      setSupervisors(users.filter(u => u.role === 'supervisor'));
      setMarketingTeam(users.filter(u => u.role === 'marketing'));
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  // Handyman management for Convert to Job
  const addHandyman = () => {
    setConvertData({
      ...convertData,
      handymen: [...convertData.handymen, { handyman_id: '', scheduled_date: '', notes: '' }]
    });
  };

  const removeHandyman = (index) => {
    if (convertData.handymen.length > 1) {
      const newHandymen = convertData.handymen.filter((_, i) => i !== index);
      setConvertData({ ...convertData, handymen: newHandymen });
    }
  };

  const updateHandyman = (index, field, value) => {
    const newHandymen = [...convertData.handymen];
    newHandymen[index] = { ...newHandymen[index], [field]: value };
    setConvertData({ ...convertData, handymen: newHandymen });
  };

  const toggleMarketingMember = (userId) => {
    const currentIds = convertData.marketing_ids;
    if (currentIds.includes(userId)) {
      setConvertData({ ...convertData, marketing_ids: currentIds.filter(id => id !== userId) });
    } else {
      setConvertData({ ...convertData, marketing_ids: [...currentIds, userId] });
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axiosInstance.get(`/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${quoteData.quote?.quote_number || quoteId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Open email compose dialog
  const openEmailCompose = () => {
    setShowEmailDialog(true);
  };

  // Open SMS compose dialog
  const openSmsCompose = () => {
    setShowSmsDialog(true);
  };

  // Send composed email
  const handleSendComposedEmail = async () => {
    if (!emailData.to) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setSending(true);
    try {
      await axiosInstance.post(`/quotes/${quoteId}/send-custom`, {
        to_email: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
        include_pdf: emailData.includePdf,
        base_url: window.location.origin
      });
      toast.success('Email sent successfully');
      setShowEmailDialog(false);
      fetchQuoteData();
    } catch (error) {
      // Fallback to standard send if custom endpoint doesn't exist
      try {
        await axiosInstance.post(`/quotes/${quoteId}/send`, {
          base_url: window.location.origin
        });
        toast.success('Quote sent to customer');
        setShowEmailDialog(false);
        fetchQuoteData();
      } catch (err) {
        toast.error('Failed to send email');
      }
    } finally {
      setSending(false);
    }
  };

  // Send composed SMS
  const handleSendComposedSms = async () => {
    if (!smsData.to) {
      toast.error('Please enter recipient phone number');
      return;
    }
    
    setSending(true);
    try {
      await axiosInstance.post(`/quotes/${quoteId}/send-sms`, {
        to_phone: smsData.to,
        message: smsData.message
      });
      toast.success('SMS sent successfully');
      setShowSmsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMS integration not configured yet');
    } finally {
      setSending(false);
    }
  };

  // Create Invoice from Quote - Go directly to Invoice Template
  const handleCreateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const response = await axiosInstance.post(`/quotes/${quoteId}/create-invoice`, {
        due_days: 30,
        notes: `Invoice generated from Quote #${quoteData?.quote?.quote_number || quoteId.slice(0, 8).toUpperCase()}`,
        include_payment_terms: true
      });
      
      // Fetch the full invoice data
      const invoiceResponse = await axiosInstance.get(`/invoices/${response.data.invoice_id}`);
      setCreatedInvoice(invoiceResponse.data);
      
      toast.success(`Invoice #${response.data.invoice_number} created!`);
      
      // Directly open the Invoice View (skip dialog)
      setShowInvoiceView(true);
      
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleAdminApprove = async () => {
    const adminUser = JSON.parse(localStorage.getItem('user') || '{}');
    
    setApproving(true);
    try {
      await axiosInstance.post(`/quotes/${quoteId}/admin-approve`, {
        approval_method: approvalMethod,
        notes: approveNote,
        admin_name: adminUser.name || adminUser.email || 'Admin User',
        admin_email: adminUser.email || 'admin@system'
      });
      
      toast.success('Quote approved successfully!');
      setShowApproveDialog(false);
      setApproveNote('');
      setApprovalMethod('other_communication');
      fetchQuoteData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  const handleConvertToJob = async () => {
    // Validate at least one handyman with date
    const validHandymen = convertData.handymen.filter(h => h.handyman_id && h.scheduled_date);
    if (validHandymen.length === 0) {
      toast.error('Please assign at least one handyman with a scheduled date');
      return;
    }

    try {
      // Use the first handyman for the main job creation (backend compatibility)
      const primaryHandyman = validHandymen[0];
      
      await axiosInstance.post(`/quotes/${quoteId}/convert-to-job`, {
        handyman_id: primaryHandyman.handyman_id,
        scheduled_date: new Date(primaryHandyman.scheduled_date).toISOString(),
        additional_handymen: validHandymen.slice(1).map(h => ({
          handyman_id: h.handyman_id,
          scheduled_date: new Date(h.scheduled_date).toISOString(),
          notes: h.notes
        })),
        supervisor_id: convertData.supervisor_id || null,
        marketing_ids: convertData.marketing_ids,
        job_notes: convertData.job_notes
      });
      
      toast.success('Quote converted to job successfully!');
      setShowConvertDialog(false);
      setConvertData({
        handymen: [{ handyman_id: '', scheduled_date: '', notes: '' }],
        supervisor_id: '',
        marketing_ids: [],
        job_notes: ''
      });
      fetchQuoteData();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to convert quote to job');
    }
  };

  const toggleItemExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700 border-gray-300',
      sent: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      approved: 'bg-green-100 text-green-700 border-green-300',
      converted: 'bg-blue-100 text-blue-700 border-blue-300',
      accepted: 'bg-blue-100 text-blue-700 border-blue-300',
      rejected: 'bg-red-100 text-red-700 border-red-300',
      revision_requested: 'bg-orange-100 text-orange-700 border-orange-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      sent: 'Awaiting Response',
      approved: 'Approved',
      converted: 'Converted to Job',
      accepted: 'Converted to Job',
      rejected: 'Rejected',
      revision_requested: 'Changes Requested'
    };
    return labels[status] || status;
  };

  const formatApprovalMethod = (method) => {
    const methods = {
      'other_communication': 'Phone/In-Person',
      'email': 'Email Confirmation',
      'portal': 'Customer Portal'
    };
    return methods[method] || method;
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
            </div>
          ) : quoteData?.quote ? (
            <>
              {/* Header Bar */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Quote #{quoteData.quote.quote_number || quoteData.quote.id?.slice(0, 8).toUpperCase()}</h2>
                    <p className="text-teal-100 text-sm">{quoteData.company?.company_name || 'Redefyne Interior and Exterior'}</p>
                  </div>
                  <Badge className={`${getStatusColor(quoteData.quote.status)} text-sm px-3 py-1`}>
                    {getStatusLabel(quoteData.quote.status)}
                  </Badge>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {/* Convert to Job - For approved quotes */}
                  {quoteData.quote.status === 'approved' && (
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => setShowConvertDialog(true)}
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      data-testid="convert-job-btn"
                    >
                      <Briefcase className="w-4 h-4 mr-1" />
                      Convert to Job
                    </Button>
                  )}
                  
                  {/* Create Invoice - For approved or converted quotes */}
                  {(quoteData.quote.status === 'approved' || quoteData.quote.status === 'converted') && (
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={handleCreateInvoice}
                      disabled={creatingInvoice}
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid="create-invoice-btn"
                    >
                      {creatingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
                      Create Invoice
                    </Button>
                  )}
                  
                  {/* Send Email - Always available */}
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={openEmailCompose}
                    data-testid="send-email-btn"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Send Email
                  </Button>
                  
                  {/* Send SMS - Always available */}
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={openSmsCompose}
                    data-testid="send-sms-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send SMS
                  </Button>
                  
                  {/* Edit Quote - Always available */}
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      onEdit?.(quoteData.quote);
                    }}
                    data-testid="edit-quote-btn"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Quote
                  </Button>
                  
                  {/* Secondary Actions */}
                  <div className="flex-1" />
                  
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={handleDownloadPDF}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download PDF
                  </Button>
                  
                  {(quoteData.quote.status === 'sent' || quoteData.quote.status === 'revision_requested') && (
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={() => setShowApproveDialog(true)}
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve Quote
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Customer & Property Panel */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Customer Info */}
                  <Card className="border-l-4 border-l-teal-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <User className="w-4 h-4 text-[#595959]" />
                        Customer Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-lg">{quoteData.quote.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        {quoteData.quote.customer_phone || 'N/A'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        {quoteData.quote.customer_email}
                      </div>
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        {quoteData.quote.customer_address || 'N/A'}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Property Info */}
                  <Card className="border-l-4 border-l-blue-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building className="w-4 h-4 text-blue-600" />
                        Property / Service Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {quoteData.property ? (
                        <>
                          <div className="font-medium">{quoteData.property.property_name || 'Property'}</div>
                          <div className="text-sm text-gray-600">{quoteData.property.full_address}</div>
                          {quoteData.property.city && (
                            <div className="text-sm text-gray-500">{quoteData.property.city}</div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-600">{quoteData.quote.service_address || quoteData.quote.customer_address || 'N/A'}</div>
                      )}
                      {quoteData.quote.service_type && (
                        <div className="mt-2 pt-2 border-t">
                          <span className="text-xs text-gray-500">Service Type:</span>
                          <p className="font-medium">{quoteData.quote.service_type}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Line Items */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Line Items</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-600 px-3 py-2 bg-gray-50 rounded-t-lg">
                      <div className="col-span-5">Product / Service</div>
                      <div className="col-span-2 text-center">Qty</div>
                      <div className="col-span-2 text-right">Unit Price</div>
                      <div className="col-span-3 text-right">Total</div>
                    </div>

                    {/* Line Items */}
                    <div className="divide-y">
                      {quoteData.quote.items?.map((item, index) => (
                        <div key={item.id || index} className="py-3">
                          {/* Main Row */}
                          <div className="grid grid-cols-12 gap-2 items-start px-3">
                            <div className="col-span-5">
                              <div className="flex items-start gap-2">
                                <span className="font-medium">{item.name}</span>
                                {item.is_optional && (
                                  <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700">Optional</Badge>
                                )}
                              </div>
                              
                              {/* Expand/Collapse for Description & Photos */}
                              {(item.description || (item.photos && item.photos.length > 0)) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs text-gray-500 p-0 h-auto mt-1"
                                  onClick={() => toggleItemExpand(item.id || index)}
                                >
                                  {expandedItems[item.id || index] ? (
                                    <>
                                      <ChevronUp className="w-3 h-3 mr-1" />
                                      Hide Details
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3 mr-1" />
                                      Show Details
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                            <div className="col-span-2 text-center">{item.quantity} {item.unit || 'unit'}</div>
                            <div className="col-span-2 text-right">${item.unit_price?.toFixed(2)}</div>
                            <div className="col-span-3 text-right font-medium">
                              ${item.total?.toFixed(2)}
                            </div>
                          </div>

                          {/* Description & Photos (Expanded) */}
                          {expandedItems[item.id || index] && (
                            <div className="mt-3 ml-3 p-3 bg-gray-50 rounded-lg">
                              {item.description && (
                                <div className="mb-3">
                                  <p className="text-xs text-gray-500 mb-1">Description:</p>
                                  <div 
                                    className="text-sm text-gray-700 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: item.description }}
                                  />
                                </div>
                              )}
                              
                              {item.photos && item.photos.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-2">Photos:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {item.photos.map((photo, photoIdx) => (
                                      <div 
                                        key={photoIdx}
                                        className="w-20 h-20 rounded-lg overflow-hidden border cursor-pointer hover:ring-2 hover:ring-teal-400 transition-all"
                                        onClick={() => setShowImagePreview(photo)}
                                      >
                                        <img 
                                          src={photo} 
                                          alt={`Photo ${photoIdx + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Totals */}
                    <div className="mt-4 pt-4 border-t space-y-2">
                      <div className="flex justify-between text-sm px-3">
                        <span className="text-gray-600">Subtotal</span>
                        <span>${quoteData.quote.subtotal?.toFixed(2)}</span>
                      </div>
                      {quoteData.quote.discount > 0 && (
                        <div className="flex justify-between text-sm px-3">
                          <span className="text-gray-600">
                            Discount {quoteData.quote.discount_type === 'percent' && `(${quoteData.quote.discount_percent}%)`}
                          </span>
                          <span className="text-red-600">-${quoteData.quote.discount?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm px-3">
                        <span className="text-gray-600">Tax ({quoteData.quote.tax_percent}%)</span>
                        <span>${quoteData.quote.tax?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold px-3 pt-2 border-t">
                        <span className="text-gray-900">Grand Total</span>
                        <span className="text-[#595959]">${quoteData.quote.total_amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Terms */}
                {quoteData.quote.payment_terms && (
                  <Card className="border-l-4 border-l-green-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        Payment Terms
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Advance</p>
                          <p className="text-lg font-bold text-green-700">
                            ${quoteData.quote.payment_terms.advance_amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {quoteData.quote.payment_terms.advance_type === 'percent' 
                              ? `${quoteData.quote.payment_terms.advance_value}%`
                              : 'Fixed'}
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Interim</p>
                          <p className="text-lg font-bold text-green-700">
                            ${quoteData.quote.payment_terms.interim_amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {quoteData.quote.payment_terms.interim_type === 'percent' 
                              ? `${quoteData.quote.payment_terms.interim_value}%`
                              : 'Fixed'}
                          </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-gray-500">Final</p>
                          <p className="text-lg font-bold text-green-700">
                            ${quoteData.quote.payment_terms.final_amount?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {quoteData.quote.payment_terms.final_type === 'percent' 
                              ? `${quoteData.quote.payment_terms.final_value}%`
                              : 'Fixed'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Contract/Disclaimer */}
                {quoteData.quote.contract_disclaimer && (
                  <Card className="border-l-4 border-l-amber-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-600" />
                        Terms & Conditions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="text-sm text-gray-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: quoteData.quote.contract_disclaimer }}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {quoteData.quote.notes && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-600" />
                        Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{quoteData.quote.notes}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Attachments */}
                {quoteData.quote.attachments && quoteData.quote.attachments.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Image className="w-4 h-4 text-gray-600" />
                        Attachments ({quoteData.quote.attachments.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-3">
                        {quoteData.quote.attachments.map((attachment, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                            {attachment.type === 'image' ? (
                              <Image className="w-4 h-4 text-blue-500" />
                            ) : (
                              <FileText className="w-4 h-4 text-orange-500" />
                            )}
                            <span className="text-sm">{attachment.filename}</span>
                            <a 
                              href={attachment.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#595959] hover:text-[#595959]"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Approval History */}
                {quoteData.approval_history && quoteData.approval_history.length > 0 && (
                  <Card className="border-l-4 border-l-purple-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="w-4 h-4 text-purple-600" />
                        Approval History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {quoteData.approval_history.map((entry, idx) => (
                          <div key={entry.id || idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              entry.action === 'approved' ? 'bg-green-100 text-green-600' :
                              entry.action === 'rejected' ? 'bg-red-100 text-red-600' :
                              entry.action === 'sent' ? 'bg-blue-100 text-blue-600' :
                              'bg-amber-100 text-amber-600'
                            }`}>
                              {entry.action === 'approved' && <CheckCircle className="w-4 h-4" />}
                              {entry.action === 'rejected' && <AlertCircle className="w-4 h-4" />}
                              {entry.action === 'sent' && <Send className="w-4 h-4" />}
                              {entry.action === 'revision_requested' && <Clock className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {entry.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </p>
                              <p className="text-xs text-gray-500">
                                By {entry.approved_by_name} ({entry.approved_by_type}) via {formatApprovalMethod(entry.approval_method)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {formatDateTime(entry.timestamp)}
                              </p>
                              {entry.approval_notes && (
                                <p className="text-xs text-gray-600 mt-1 italic">&ldquo;{entry.approval_notes}&rdquo;</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Job Link */}
                {quoteData.job && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Briefcase className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">Linked Job</p>
                            <p className="text-sm text-gray-600">Job ID: {quoteData.job.id?.slice(0, 8)}</p>
                          </div>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">
                          {quoteData.job.status?.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Quote Meta */}
                <div className="text-xs text-gray-400 text-center pt-4 border-t">
                  <p>Created: {formatDateTime(quoteData.quote.created_at)}</p>
                  {quoteData.quote.sent_at && <p>Sent: {formatDateTime(quoteData.quote.sent_at)}</p>}
                  <p>Valid Until: {formatDate(quoteData.quote.valid_until)}</p>
                  <p>Prepared By: {quoteData.quote.prepared_by}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-96">
              <p className="text-gray-500">Quote not found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!showImagePreview} onOpenChange={() => setShowImagePreview(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={() => setShowImagePreview(null)}
            >
              <X className="w-4 h-4" />
            </Button>
            <img 
              src={showImagePreview} 
              alt="Preview" 
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve Quote
            </DialogTitle>
            <DialogDescription>
              Record approval on behalf of customer who confirmed via phone, email, or other channel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Approval Method *</Label>
              <Select value={approvalMethod} onValueChange={setApprovalMethod}>
                <SelectTrigger className="mt-1" data-testid="view-approval-method-select">
                  <SelectValue placeholder="Select how customer approved" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="other_communication">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone / In-Person
                    </div>
                  </SelectItem>
                  <SelectItem value="email">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email Confirmation
                    </div>
                  </SelectItem>
                  <SelectItem value="portal">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Portal (Admin Action)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Approval Notes</Label>
              <Textarea
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="E.g., Customer confirmed via phone call..."
                rows={3}
                className="mt-1"
                data-testid="view-approval-notes"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleAdminApprove} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={approving}
                data-testid="view-confirm-approve"
              >
                {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Confirm & Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Job Dialog - Enhanced with Multiple Assignments */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#595959]" />
              Convert Quote to Job
            </DialogTitle>
            <DialogDescription>
              Assign team members and schedule the job. Add multiple handymen if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Handymen Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Wrench className="w-4 h-4 text-blue-600" />
                  Handymen Assignment *
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addHandyman}
                  className="text-blue-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Handyman
                </Button>
              </div>
              
              <div className="space-y-3">
                {convertData.handymen.map((assignment, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Handyman #{index + 1}
                      </span>
                      {convertData.handymen.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHandyman(index)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Select Handyman *</Label>
                        <Select 
                          value={assignment.handyman_id} 
                          onValueChange={(value) => updateHandyman(index, 'handyman_id', value)}
                        >
                          <SelectTrigger className="mt-1" data-testid={`handyman-select-${index}`}>
                            <SelectValue placeholder="Select handyman" />
                          </SelectTrigger>
                          <SelectContent>
                            {handymen.map((handyman) => (
                              <SelectItem 
                                key={handyman.id} 
                                value={handyman.id}
                                disabled={convertData.handymen.some((h, i) => i !== index && h.handyman_id === handyman.id)}
                              >
                                {handyman.name} - {handyman.skills?.slice(0, 2).join(', ') || 'General'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Schedule Date & Time *</Label>
                        <Input
                          type="datetime-local"
                          value={assignment.scheduled_date}
                          onChange={(e) => updateHandyman(index, 'scheduled_date', e.target.value)}
                          className="mt-1"
                          data-testid={`schedule-date-${index}`}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs">Assignment Notes</Label>
                      <Input
                        value={assignment.notes}
                        onChange={(e) => updateHandyman(index, 'notes', e.target.value)}
                        placeholder="Special instructions for this handyman..."
                        className="mt-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Supervisor Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <UserCheck className="w-4 h-4 text-purple-600" />
                Supervisor (Optional)
              </Label>
              <Select 
                value={convertData.supervisor_id || "none"} 
                onValueChange={(value) => setConvertData({...convertData, supervisor_id: value === "none" ? "" : value})}
              >
                <SelectTrigger data-testid="supervisor-select">
                  <SelectValue placeholder="Select supervisor to oversee this job" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {supervisors.map((supervisor) => (
                    <SelectItem key={supervisor.id} value={supervisor.id}>
                      {supervisor.name || supervisor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Supervisor will be notified and can track job progress</p>
            </div>

            {/* Marketing Team Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Megaphone className="w-4 h-4 text-orange-600" />
                Marketing Team (Optional)
              </Label>
              {marketingTeam.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50">
                  {marketingTeam.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100 cursor-pointer"
                      onClick={() => toggleMarketingMember(member.id)}
                    >
                      <Checkbox 
                        checked={convertData.marketing_ids.includes(member.id)}
                        onCheckedChange={() => toggleMarketingMember(member.id)}
                      />
                      <span className="text-sm">{member.name || member.email}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 p-3 border rounded-lg bg-gray-50">
                  No marketing team members available
                </p>
              )}
              <p className="text-xs text-gray-500">Marketing team will be notified for coordination</p>
            </div>

            {/* Job Notes */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-600" />
                Job Notes
              </Label>
              <Textarea
                value={convertData.job_notes}
                onChange={(e) => setConvertData({...convertData, job_notes: e.target.value})}
                placeholder="Any additional notes or instructions for this job..."
                rows={3}
              />
            </div>

            {/* Summary */}
            <div className="p-3 bg-gray-100 rounded-lg border border-gray-300">
              <p className="text-sm font-medium text-teal-800">Assignment Summary:</p>
              <ul className="text-sm text-[#595959] mt-1 space-y-1">
                <li>• {convertData.handymen.filter(h => h.handyman_id).length} Handyman(s) assigned</li>
                <li>• Supervisor: {convertData.supervisor_id ? supervisors.find(s => s.id === convertData.supervisor_id)?.name || 'Selected' : 'None'}</li>
                <li>• Marketing: {convertData.marketing_ids.length > 0 ? `${convertData.marketing_ids.length} member(s)` : 'None'}</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowConvertDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleConvertToJob} 
                className="flex-1 bg-[#595959] hover:bg-[#404040]"
                disabled={!convertData.handymen.some(h => h.handyman_id && h.scheduled_date)}
                data-testid="view-confirm-convert"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Create Job
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#595959]" />
              Compose Email
            </DialogTitle>
            <DialogDescription>
              Send quote details to the customer via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To *</Label>
              <Input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({...emailData, to: e.target.value})}
                placeholder="customer@email.com"
                data-testid="email-to-input"
              />
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder="Email subject"
                data-testid="email-subject-input"
              />
            </div>
            
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailData.message}
                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                placeholder="Email message..."
                rows={8}
                className="font-mono text-sm"
                data-testid="email-message-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-pdf"
                checked={emailData.includePdf}
                onChange={(e) => setEmailData({...emailData, includePdf: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="include-pdf" className="text-sm cursor-pointer">
                Include Quote PDF as attachment
              </Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEmailDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSendComposedEmail}
                className="flex-1 bg-[#595959] hover:bg-[#404040]"
                disabled={sending || !emailData.to}
                data-testid="send-email-confirm"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Compose Dialog */}
      <Dialog open={showSmsDialog} onOpenChange={setShowSmsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-[#595959]" />
              Compose SMS
            </DialogTitle>
            <DialogDescription>
              Send a text message to the customer about this quote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To (Phone Number) *</Label>
              <Input
                type="tel"
                value={smsData.to}
                onChange={(e) => setSmsData({...smsData, to: e.target.value})}
                placeholder="+1 (555) 123-4567"
                data-testid="sms-to-input"
              />
            </div>
            
            <div>
              <Label>Message</Label>
              <Textarea
                value={smsData.message}
                onChange={(e) => setSmsData({...smsData, message: e.target.value})}
                placeholder="SMS message..."
                rows={4}
                maxLength={160}
                data-testid="sms-message-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {smsData.message.length}/160 characters
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSmsDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSendComposedSms}
                className="flex-1 bg-[#595959] hover:bg-[#404040]"
                disabled={sending || !smsData.to}
                data-testid="send-sms-confirm"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Send SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog - Editable */}
      <InvoiceView
        open={showInvoiceView}
        onOpenChange={setShowInvoiceView}
        invoiceId={createdInvoice?.id}
        invoiceData={createdInvoice}
        onRefresh={onRefresh}
        editable={true}
        quoteData={quoteData?.quote}
      />
    </>
  );
}
